import map from 'lodash/map'
import React, { FC, useContext } from 'react'
import styled, { css } from 'styled-components'

import AngleRight from '@Assets/SVG/AngleRight'
import ClockIcon from '@Assets/SVG/Clock'
import CloseIcon from '@Assets/SVG/Close'
import { SVGIcon } from '@Components/Icons'
import { Link, LinkProps } from '@Components/Link'
import { NavigationEvent } from '@Events/Navigation'
import { MetadataContext } from '@Globals/Metadata'
import {
  addPreviousSearch,
  PreviousSearch,
  PreviousSearchItemTypes,
  removePreviousSearchItem,
} from '@Globals/Search/PreviousSearches'
import { SearchSelection } from '@Globals/Search/Selection'
import { up } from '@Globals/Style/breakpoints'
import {
  box,
  flexCenterChildren,
  sectionInner,
  smoothScroll,
} from '@Globals/Style/mixins'
import { useTranslation } from '@Globals/Translations'

interface PreviousSearchesProps {
  previousSearches: PreviousSearch[]
  selection: SearchSelection
}

const StyledDivideIcon = styled.div`
  ${flexCenterChildren}
  display: inline-flex;
  margin: 0 5px;
  & > svg {
    fill: ${({ theme }) => theme.colors.grey}
  }
  ${up('desktop')} {
    ${box({ width: '8px' })}
  }
`

const SearchText: FC<PreviousSearch> = ({
  brand,
  parentTitle,
  title,
  type,
}: PreviousSearch) => {
  if (type === PreviousSearchItemTypes.FreeText) {
    return <span>{`"${title}"`}</span>
  }
  if (type === PreviousSearchItemTypes.Product) {
    return <span>{`${brand} - ${title}`}</span>
  }
  if (type === PreviousSearchItemTypes.Category && parentTitle) {
    return (
      <span>
        {parentTitle}
        <StyledDivideIcon>
          <SVGIcon icon={AngleRight} size="8px" />
        </StyledDivideIcon>
        <span>{title}</span>
      </span>
    )
  }
  return <span>{title}</span>
}

interface PreviousSearchItemProps extends PreviousSearch {
  selected: boolean
}

type StyledPreviousSearchesItemProps = LinkProps & { selected: boolean }

const StyledPreviousSearchesItem = styled(Link)<StyledPreviousSearchesItemProps>`
  position: relative;
  display: flex;
  align-items: center;
  padding: 8px 50px 8px 0;
  min-height: 40px;
  cursor: pointer;
  ${({ selected, theme }) => selected && css`
    background-color: ${theme.colors.greyLightest};
  `}
`

const StyledClockIcon = styled.div`
  ${flexCenterChildren}
  flex-shrink: 0;
  margin-right: 8px;
  cursor: pointer;
  & > svg {
    fill: ${({ theme }) => theme.colors.grey};
  }
`

const StyledTextWrap = styled.div`
  flex-grow: 2;
`

const StyledInnerText = styled.div`
  font-size: 0;
  & > span {
    display: inline;
    font-size: 1rem;
  }
`

const StyledRemoveIcon = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  ${box({ width: '40px' })}
  ${flexCenterChildren}
  flex-shrink: 0;
  padding: 14px;

  & > svg {
    ${box({ width: '12px' })}
    fill: ${({ theme }) => theme.colors.grey};
  }
`

const PreviousSearchItem: FC<PreviousSearchItemProps> = (
  {
    selected,
    ...previousSearch
  }: PreviousSearchItemProps,
) => {
  const { redirectURL, storeURL } = useContext(MetadataContext)
  const {
    id,
    slug,
    title,
    type,
  } = previousSearch

  const getURL = () => {
    if (type === PreviousSearchItemTypes.FreeText) {
      return storeURL('catalogsearch/result/', new URLSearchParams({ q: title }))
    }
    if (type === PreviousSearchItemTypes.Brand) {
      return storeURL(slug)
    }
    return redirectURL(type, id, true)
  }

  return (
    <StyledPreviousSearchesItem
      event={new NavigationEvent({
        attributes: {
          dest_page_type: type === PreviousSearchItemTypes.FreeText ? 'searchresults' : type,
          dest_page_type_id: type === PreviousSearchItemTypes.FreeText ? '' : id || '',
          search_query: '',
          source_id: 'site_search_previous_searches',
        },
        eventName: 'nav_link_click',
      })}
      href={getURL()}
      onClick={() => addPreviousSearch(previousSearch)}
      selected={selected}
    >
      <StyledClockIcon>
        <SVGIcon icon={ClockIcon} size="16px" />
      </StyledClockIcon>
      <StyledTextWrap>
        <StyledInnerText>
          <SearchText {...previousSearch} />
        </StyledInnerText>
      </StyledTextWrap>
      <StyledRemoveIcon
        onClick={(e) => {
          e.stopPropagation()
          e.preventDefault()
          removePreviousSearchItem(type, id)
        }}
      >
        <SVGIcon icon={CloseIcon} />
      </StyledRemoveIcon>
    </StyledPreviousSearchesItem>
  )
}

const StyledPreviousSearches = styled.div`
  padding: 20px 10px 100px 20px;
  height: calc(100% - 60px);
  overflow: auto;
  ${smoothScroll}

  ${up('desktop')} {
    ${sectionInner}
    min-height: 260px;
    padding: 20px;
    background-color: #fff;
    /* TODO: Add color to theme (greyLightest darkened 5%) */
    border: 1px solid #e8e8e8;
    border-top: none;
    box-shadow: 0 10px 15px 0 rgba(0,0,0,0.05)
  }
`

const StyledTitle = styled.span`
  display: block;
  font-size: 0.75rem;
  text-transform: uppercase;
  margin-bottom: 5px;
  ${up('desktop')} {
    margin: 10px;
    padding-bottom: 4px;
    max-width: 60%;
    font-weight: bold;
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey};
  }
`

export const PreviousSearches: FC<PreviousSearchesProps> = ({
  previousSearches,
  selection,
}: PreviousSearchesProps) => {
  const { t } = useTranslation()

  return (
    <StyledPreviousSearches onClick={e => e.stopPropagation()}>
      <StyledTitle>{t('header:PREVIOUS_SEARCHES')}</StyledTitle>
      {map(previousSearches, p => (
        <PreviousSearchItem
          key={`${p.type}: ${p.slug}`}
          selected={selection.type === 'previoussearch' && selection.id === p.id}
          {...p}
        />
      ))}
    </StyledPreviousSearches>
  )
}
